import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Avatar, Tooltip, Chip, AvatarGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { calculateEndDate } from '../utils/dateUtils';

const COLUMN_WIDTH = 140;

const TimelineContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  position: 'relative', // For sticky positioning context
}));

const DayColumn = styled(Box)(({ theme, isToday }) => ({
  width: COLUMN_WIDTH,
  minWidth: COLUMN_WIDTH,
  flexShrink: 0,
  padding: theme.spacing(1),
  textAlign: 'center',
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: isToday ? theme.palette.action.hover : 'transparent',
  '&:last-child': {
    borderRight: 'none',
  },
}));

const ProjectRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 0),
  position: 'relative',
  minHeight: 100,
  minWidth: 'fit-content', // Ensure it doesn't shrink
}));

const ProjectInfo = styled(Box)(({ theme }) => ({
  width: 60,
  minWidth: 60,
  padding: theme.spacing(1),
  borderRight: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'sticky',
  left: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 10,
  boxShadow: '2px 0 4px rgba(0,0,0,0.05)', // Subtle shadow to show separation
}));

const TimelineGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  // No flex: 1, let it be determined by children
}));

const TimelineCell = styled(Box)(({ theme, isToday }) => ({
  width: COLUMN_WIDTH,
  minWidth: COLUMN_WIDTH,
  flexShrink: 0,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: isToday ? 'action.hover' : 'transparent',
  position: 'relative',
  '&:last-child': {
    borderRight: 'none',
  },
}));

const ProjectBar = styled(Box)(({ theme, color }) => ({
  position: 'absolute',
  height: 28,
  backgroundColor: color || theme.palette.primary.main,
  borderRadius: 4,
  top: 8,
  opacity: 0.9,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  color: theme.palette.primary.contrastText,
  fontSize: '0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxShadow: theme.shadows[2],
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    opacity: 1,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  }
}));

const DashboardTimeline = ({ activities, projects, purchaseOrders }) => {
  const navigate = useNavigate();
  // Calculate date range: Today - 2 days to Today + 21 days (3 weeks)
  const dates = useMemo(() => {
    const result = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = -2; i <= 21; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      result.push(date);
    }
    return result;
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Filter projects that overlap with our timeline OR have events in the timeline
  const visibleProjects = useMemo(() => {
    const timelineStart = dates[0];
    const timelineEnd = dates[dates.length - 1];

    return projects.filter(project => {
      // Check 1: Project duration overlap
      let projectOverlap = false;
      // Use start_date if available, otherwise fall back to due_date
      const projectStartDate = project.start_date || project.due_date;
      
      if (projectStartDate) {
        const startDate = new Date(projectStartDate);
        startDate.setHours(0, 0, 0, 0);
        
        const duration = project.duration_in_days || 1;
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + duration - 1);
        
        projectOverlap = startDate <= timelineEnd && endDate >= timelineStart;
      }

      // Check 2: Activities overlap
      const hasActivities = activities.some(a => {
        if (a.project_id !== project.project_id) return false;
        if (!a.due_date) return false;
        const activityDate = new Date(a.due_date);
        activityDate.setHours(0, 0, 0, 0);
        return activityDate >= timelineStart && activityDate <= timelineEnd;
      });

      // Check 3: Purchase Orders overlap
      const hasPOs = purchaseOrders.some(po => {
        if (po.project_id !== project.project_id) return false;
        if (!po.due_date) return false;
        const poDate = new Date(po.due_date);
        poDate.setHours(0, 0, 0, 0);
        return poDate >= timelineStart && poDate <= timelineEnd;
      });

      return projectOverlap || hasActivities || hasPOs;
    });
  }, [projects, activities, purchaseOrders, dates]);

  // Filter standalone POs
  const visibleStandalonePOs = useMemo(() => {
    const timelineStart = dates[0];
    const timelineEnd = dates[dates.length - 1];
    
    return purchaseOrders.filter(po => {
      if (po.project_id) return false; // Must be standalone
      if (!po.due_date) return false;
      const poDate = new Date(po.due_date);
      poDate.setHours(0, 0, 0, 0);
      return poDate >= timelineStart && poDate <= timelineEnd;
    });
  }, [purchaseOrders, dates]);

  // Group projects by worker combination
  const groupedProjects = useMemo(() => {
    const groups = {};
    
    visibleProjects.forEach(project => {
      // Filter non-office workers
      const siteWorkers = (project.assigned_workers || [])
        .filter(w => w.role !== 'Office')
        .sort((a, b) => a.name.localeCompare(b.name));
      
      const key = siteWorkers.length > 0 
        ? siteWorkers.map(w => w.name).join(', ') 
        : 'Unassigned';
      
      if (!groups[key]) {
        groups[key] = {
          workers: siteWorkers,
          projects: []
        };
      }
      groups[key].projects.push(project);
    });
    
    // Sort groups to put Unassigned last
    const sortedGroups = Object.entries(groups).sort((a, b) => {
      if (a[0] === 'Unassigned') return 1;
      if (b[0] === 'Unassigned') return -1;
      return a[0].localeCompare(b[0]);
    });

    // Add Standalone POs group if needed
    if (visibleStandalonePOs.length > 0) {
      sortedGroups.push(['Standalone Purchase Orders', {
        workers: [],
        isStandalone: true,
        projects: [{
          project_id: 'standalone_container',
          is_standalone_container: true,
          start_date: dates[dates.length - 1],
          duration_in_days: dates.length,
          organisation_name: 'Standalone Purchase Orders'
        }]
      }]);
    }
    
    return sortedGroups;
  }, [visibleProjects, visibleStandalonePOs, dates]);

  // Group activities and POs by project and date
  const getProjectEvents = (projectId, date) => {
    const dateStr = date.toDateString();
    
    if (projectId === 'standalone_container') {
      const pos = visibleStandalonePOs.filter(po => 
        new Date(po.due_date).toDateString() === dateStr
      );
      return { activities: [], purchaseOrders: pos };
    }
    
    const projectActivities = activities.filter(a => 
      a.project_id === projectId && 
      new Date(a.due_date).toDateString() === dateStr
    );
    
    const projectPOs = purchaseOrders.filter(po => 
      po.project_id === projectId && 
      new Date(po.due_date).toDateString() === dateStr
    );
    
    return { activities: projectActivities, purchaseOrders: projectPOs };
  };

  const getProjectBarStyle = (project) => {
    // Use start_date if available, otherwise fall back to due_date
    const projectStartDate = project.start_date;
    if (!projectStartDate) return { display: 'none' };
    
    const startDate = new Date(projectStartDate);
    startDate.setHours(0, 0, 0, 0);
    
    const duration = project.duration_in_days || 1;
    const workingSat = project.working_sat || false;
    const workingSun = project.working_sun || false;
    
    const endDate = calculateEndDate(startDate, duration, workingSat, workingSun);
    
    const timelineStart = dates[0];
    
    // Calculate left offset and width in pixels
    const totalDays = dates.length;
    
    // Calculate days from timeline start
    const startDiff = (startDate - timelineStart) / (1000 * 60 * 60 * 24);
    const endDiff = (endDate - timelineStart) / (1000 * 60 * 60 * 24);
    
    let left = startDiff * COLUMN_WIDTH;
    let width = (endDiff - startDiff + 1) * COLUMN_WIDTH;
    
    // Clip to visible area
    const totalWidth = totalDays * COLUMN_WIDTH;
    
    if (left < 0) {
      width += left;
      left = 0;
    }
    
    if (left + width > totalWidth) {
      width = totalWidth - left;
    }
    
    if (width <= 0) return { display: 'none' };

    const isCompleted = project.status === 'Completed';

    return {
      left: `${left}px`,
      width: `${width}px`,
      backgroundColor: isCompleted ? '#2e7d32' : '#1976d2', // Green for completed, Blue otherwise
      opacity: isCompleted ? 0.8 : 0.9,
    };
  };

  const getProjectSegments = (project) => {
    const projectStartDate = project.start_date;
    if (!projectStartDate) return [];
    
    const startDate = new Date(projectStartDate);
    startDate.setHours(0, 0, 0, 0);
    
    const duration = project.duration_in_days || 1;
    const workingSat = project.working_sat || false;
    const workingSun = project.working_sun || false;
    
    const segments = [];
    let currentSegment = null;
    
    let workingDaysRemaining = duration;
    let currentDate = new Date(startDate);
    let safetyCounter = 0;
    
    while (workingDaysRemaining > 0 && safetyCounter < 3650) {
      safetyCounter++;
      const dayOfWeek = currentDate.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;
      
      let isWorkingDay = true;
      if (isSaturday && !workingSat) isWorkingDay = false;
      if (isSunday && !workingSun) isWorkingDay = false;
      
      if (isWorkingDay) {
        workingDaysRemaining--;
        if (!currentSegment) {
          currentSegment = { start: new Date(currentDate), end: new Date(currentDate) };
        } else {
          currentSegment.end = new Date(currentDate);
        }
      } else {
        if (currentSegment) {
          segments.push(currentSegment);
          currentSegment = null;
        }
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    const timelineStart = dates[0];
    const totalWidth = dates.length * COLUMN_WIDTH;
    
    return segments.map(segment => {
        const startDiff = (segment.start - timelineStart) / (1000 * 60 * 60 * 24);
        const endDiff = (segment.end - timelineStart) / (1000 * 60 * 60 * 24);
        
        let left = startDiff * COLUMN_WIDTH;
        let width = (endDiff - startDiff + 1) * COLUMN_WIDTH;
        
        if (left < 0) {
            width += left;
            left = 0;
        }
        
        if (left + width > totalWidth) {
            width = totalWidth - left;
        }
        
        if (width <= 0) return null;
        
        return { left, width };
    }).filter(Boolean);
  };

  // Calculate vertical positions for projects in a group based on time overlap
  const calculateProjectPositions = (projects) => {
    const positions = [];
    
    projects.forEach(project => {
      if (!project.start_date) return;
      
      const startDate = new Date(project.start_date);
      startDate.setHours(0, 0, 0, 0);
      const duration = project.duration_in_days || 1;
      const workingSat = project.working_sat || false;
      const workingSun = project.working_sun || false;
      const endDate = calculateEndDate(startDate, duration, workingSat, workingSun);
      
      // Find the first available vertical position
      let verticalPos = 0;
      let foundPosition = false;
      
      while (!foundPosition) {
        foundPosition = true;
        
        // Check if this position conflicts with any existing project
        for (const pos of positions) {
          if (pos.verticalPos === verticalPos) {
            const existingStart = new Date(pos.project.start_date);
            existingStart.setHours(0, 0, 0, 0);
            const existingDuration = pos.project.duration_in_days || 1;
            const existingWorkingSat = pos.project.working_sat || false;
            const existingWorkingSun = pos.project.working_sun || false;
            const existingEnd = calculateEndDate(existingStart, existingDuration, existingWorkingSat, existingWorkingSun);
            
            // Check for overlap
            if (startDate <= existingEnd && endDate >= existingStart) {
              foundPosition = false;
              break;
            }
          }
        }
        
        if (!foundPosition) {
          verticalPos++;
        }
      }
      
      positions.push({ project, verticalPos });
    });
    
    return positions;
  };

  // Calculate row height based on project positions and events
  const getRowHeight = (group, dates) => {
    const visibleProjects = group.projects.filter(project => {
      if (project.is_standalone_container) return false;
      const style = getProjectBarStyle(project);
      return style.display !== 'none';
    });

    // Calculate max events stack for any day
    // Note: For standalone container, there is only one project, so this correctly counts the stack depth.
    // For regular groups, if multiple projects have events on the same day, they currently overlap in the UI.
    // This calculation assumes they might stack or we just want to ensure enough space for the busiest day.
    let maxEventsStack = 0;
    dates.forEach(date => {
      let eventsInDay = 0;
      group.projects.forEach(project => {
        const { activities, purchaseOrders } = getProjectEvents(project.project_id, date);
        eventsInDay += activities.length + purchaseOrders.length;
      });
      if (eventsInDay > maxEventsStack) {
        maxEventsStack = eventsInDay;
      }
    });

    if (visibleProjects.length === 0) {
      if (maxEventsStack > 0) {
        // 16px top padding + (events * 24px per event approx) + 16px bottom padding
        // Each chip is 20px height + 4px gap = 24px
        return 16 + (maxEventsStack * 24) + 16;
      }
      return 60; // Minimum height for empty rows
    }

    // Calculate positions to find the maximum vertical position
    const positions = calculateProjectPositions(visibleProjects);
    const maxVerticalPos = positions.length > 0 ? Math.max(...positions.map(p => p.verticalPos)) : 0;
    
    // Base height: 16 (top padding) + (max position + 1) * 32 (project bar height + spacing) + 8 (bottom padding)
    const projectHeight = 16 + (maxVerticalPos + 1) * 32 + 8;
    
    // Add space for events if any
    if (maxEventsStack > 0) {
      // Events start at 8px below the last project line.
      // So add 8px + (maxEventsStack * 24) + 8px bottom padding
      return projectHeight + 8 + (maxEventsStack * 24) + 8;
    }
    
    return Math.max(projectHeight, 80); // Minimum 80px
  };

  if (visibleProjects.length === 0) {
    return null;
  }

  return (
    <TimelineContainer elevation={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6">Project Timeline</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ fontSize: 16, color: '#ff9800' }} />
            <Typography variant="caption" color="text.secondary">No RAMS</Typography>
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', minWidth: 'fit-content' }}>
          <Box sx={{ 
            width: 60, 
            minWidth: 60, 
            flexShrink: 0,
            position: 'sticky',
            left: 0,
            backgroundColor: 'background.paper',
            zIndex: 10,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 1
          }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Team
            </Typography>
          </Box> {/* Spacer for Project Info */}
          <TimelineGrid sx={{ width: `${dates.length * COLUMN_WIDTH}px`, flexShrink: 0 }}>
            {dates.map((date, index) => {
              const isToday = date.toDateString() === today.toDateString();
              return (
                <DayColumn key={index} isToday={isToday}>
                  <Typography variant="subtitle2" sx={{ fontWeight: isToday ? 'bold' : 'normal' }}>
                    {date.toLocaleDateString('en-GB', { weekday: 'short' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </Typography>
                </DayColumn>
              );
            })}
          </TimelineGrid>
        </Box>

        {/* Project Rows Grouped by Team */}
        {groupedProjects.map(([key, group]) => {
          // Check if group has any visible content (project bars or events)
          const hasContent = group.projects.some(project => {
            if (project.is_standalone_container) {
              return visibleStandalonePOs.length > 0;
            }
            
            // Check if project bar is visible
            const projectBarStyle = getProjectBarStyle(project);
            const hasVisibleBar = projectBarStyle.display !== 'none';
            
            // Check if project has any events
            const hasEvents = dates.some(date => {
              const { activities, purchaseOrders } = getProjectEvents(project.project_id, date);
              return activities.length > 0 || purchaseOrders.length > 0;
            });
            
            return hasVisibleBar || hasEvents;
          });

          // Skip groups with no visible content
          if (!hasContent) return null;

          const rowHeight = getRowHeight(group, dates);
          
          // Calculate positions for all visible projects in this group
          const visibleProjects = group.projects.filter(project => {
            if (project.is_standalone_container) return false;
            const style = getProjectBarStyle(project);
            return style.display !== 'none';
          });
          const projectPositions = calculateProjectPositions(visibleProjects);
          
          return (
          <ProjectRow key={key} sx={{ height: rowHeight, minHeight: rowHeight }}>
            <ProjectInfo>
              {group.isStandalone ? (
                <Tooltip title="Standalone Purchase Orders">
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: 'warning.main' }}>
                    <ShoppingCartIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                </Tooltip>
              ) : group.workers.length > 0 ? (
                <AvatarGroup max={4} sx={{ justifyContent: 'center' }}>
                  {group.workers.map((w, i) => (
                    <Tooltip key={i} title={`${w.name} (${w.role})`}>
                      <Avatar 
                        alt={w.name} 
                        sx={{ width: 24, height: 24, fontSize: '0.65rem' }}
                      >
                        {w.name.charAt(0)}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              ) : (
                <Tooltip title="Unassigned">
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: 'grey.400' }}>
                    U
                  </Avatar>
                </Tooltip>
              )}
            </ProjectInfo>
            
            <TimelineGrid sx={{ width: `${dates.length * COLUMN_WIDTH}px`, flexShrink: 0 }}>
              {/* Background Grid Cells */}
              {dates.map((date, index) => {
                const isToday = date.toDateString() === today.toDateString();
                return <TimelineCell key={index} isToday={isToday} />;
              })}
              
              {/* Projects and Events */}
              {group.projects.map(project => {
                const baseStyle = getProjectBarStyle(project);
                const isProjectVisible = baseStyle.display !== 'none';
                
                // Special handling for standalone container
                if (project.is_standalone_container) {
                  baseStyle.visibility = 'hidden';
                  baseStyle.pointerEvents = 'none';
                  baseStyle.boxShadow = 'none';
                  baseStyle.backgroundColor = 'transparent';
                } else if (!isProjectVisible) {
                  // If project bar is not visible, check if we have any events to show
                  const hasEvents = dates.some(date => {
                    const { activities, purchaseOrders } = getProjectEvents(project.project_id, date);
                    return activities.length > 0 || purchaseOrders.length > 0;
                  });
                  
                  // If no visible bar AND no events, skip rendering
                  if (!hasEvents) return null;
                }

                let style = { ...baseStyle };
                if (!project.is_standalone_container && isProjectVisible) {
                  // Find the calculated position for this project
                  const position = projectPositions.find(p => p.project.project_id === project.project_id);
                  const verticalPos = position ? position.verticalPos : 0;
                  style.top = `${8 + verticalPos * 32}px`;
                }

                const segments = (!project.is_standalone_container && isProjectVisible) 
                  ? getProjectSegments(project) 
                  : [];

                return (
                  <React.Fragment key={project.project_id}>
                    {!project.is_standalone_container && isProjectVisible && segments.map((segment, i) => (
                      <Tooltip key={i} title={project.quote_reference || ''}>
                        <ProjectBar 
                          style={{
                            ...style,
                            left: `${segment.left}px`,
                            width: `${segment.width}px`
                          }}
                          onClick={() => navigate(`/projects/${project.project_id}`)}
                        >
                          <>
                            {project.status === 'Completed' && <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, color: 'inherit' }} />}
                            {!project.has_rams && <WarningIcon sx={{ fontSize: 14, mr: 0.5, color: '#ff9800' }} />}
                            {project.quote_reference || ''} {project.organisation_name} - {project.city}
                          </>
                        </ProjectBar>
                      </Tooltip>
                    ))}
                    {project.is_standalone_container && (
                      <Box style={baseStyle} />
                    )}

                    {/* Events for this project */}
                    {dates.map((date, index) => {
                      const { activities, purchaseOrders } = getProjectEvents(project.project_id, date);
                      if (activities.length === 0 && purchaseOrders.length === 0) return null;
                      
                      const left = (index * COLUMN_WIDTH) + (COLUMN_WIDTH / 2);

                      return (
                        <Box 
                          key={`${project.project_id}-${index}`} 
                          sx={{ 
                            position: 'absolute', 
                            left: `${left}px`, 
                            top: `${8 + (projectPositions.length > 0 ? Math.max(...projectPositions.map(p => p.verticalPos)) + 1 : 0) * 32 + 8}px`, 
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            alignItems: 'center',
                            width: COLUMN_WIDTH - 8 // Constrain width
                          }}
                        >
                          {activities.map(activity => (
                            <Tooltip key={activity.activity_id} title={`Activity: ${activity.title}${activity.is_completed ? ' (Completed)' : ''}`}>
                              <Chip 
                                icon={activity.is_completed ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <AssignmentIcon sx={{ fontSize: 14 }} />} 
                                label={activity.title} 
                                size="small" 
                                color={activity.is_completed ? "success" : "secondary"} 
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.65rem', 
                                  maxWidth: '100%',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  opacity: activity.is_completed ? 0.8 : 1,
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    opacity: 1,
                                  }
                                }}
                                onClick={() => navigate(`/projects/${project.project_id}?tab=activities&activityId=${activity.activity_id}`)}
                              />
                            </Tooltip>
                          ))}
                          {purchaseOrders.map(po => (
                            <Tooltip key={po.purchase_order_id} title={`PO: ${po.reference || po.purchase_order_id}${po.status === 'Completed' ? ' (Completed)' : ''}`}>
                              <Chip 
                                icon={po.status === 'Completed' ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <ShoppingCartIcon sx={{ fontSize: 14 }} />} 
                                label={po.reference || `PO #${po.purchase_order_id}`} 
                                size="small" 
                                color={po.status === 'Completed' ? "success" : "warning"} 
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.65rem', 
                                  maxWidth: '100%',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  opacity: po.status === 'Completed' ? 0.8 : 1,
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    opacity: 1,
                                  }
                                }}
                                onClick={() => navigate(`/purchase-orders/${po.purchase_order_id}`)}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TimelineGrid>
          </ProjectRow>
          );
        })}
      </Box>
    </TimelineContainer>
  );
};

export default DashboardTimeline;
